import {
  appFormsTreeAreaNodeMapExtended,
  appFormsTreeAreaNodesExtended,
  appFormsTreeFieldOptionsExtended,
  appFormsTreeFieldOptionsMapExtended,
  AREA_ID_PREFIX,
  createAppFormsTreeVersionedAccessor,
} from 'app/services/autoLockForms/autoLockForms';
import { AutoLockFormsLevelType } from 'app/services/autoLockForms/autoLockForms.constants';
import {
  normalizeLegacyAppFormsTreeId,
  normalizeLegacyAppFormsTreeIds,
} from 'app/services/autoLockForms/autoLockForms.utils';
import UserFriendlyExpressionBuilder from 'app/services/formula/parser/userFriendlyExpressionBuilder';
import _ from 'lodash';

import { FieldType } from 'constants/formGenerator';
import { formatExpression } from 'utils/expressions';

import { HIDDEN_FIELD_PREFIX, visibilityModeOptions } from './AutoLockRules.constants';

export const getSiblingsInOrder = (rulesMap, parentId) =>
  _.orderBy(
    Object.values(rulesMap).filter(rule => (rule.parentId || null) === parentId),
    'orderNumber',
    ['asc'],
  );

export const isDescendantOf = (rulesMap, candidateAncestorId, nodeId) => {
  const currentParentId = rulesMap[nodeId]?.parentId;

  if (!currentParentId) {
    return false;
  }

  if (currentParentId === candidateAncestorId) {
    return true;
  }

  return isDescendantOf(rulesMap, candidateAncestorId, currentParentId);
};

export const getAddChildButtonTitle = hasFieldsOrForms => {
  if (hasFieldsOrForms) {
    return "You can't add a child rule while Fields or Forms are set for this rule";
  }

  return 'Add child rule';
};

export const getDeleteDescription = hasChild => {
  if (hasChild) {
    return 'This will also delete all child rules. Are you sure you want to delete this rule?';
  }

  return 'Are you sure you want to delete this rule?';
};

export const stripHiddenPrefix = field =>
  field.startsWith(HIDDEN_FIELD_PREFIX) ? field.slice(HIDDEN_FIELD_PREFIX.length) : field;

export const checkIsHiddenField = field => field.startsWith(HIDDEN_FIELD_PREFIX);

const getAreaLevelType = rawAreaId => {
  const areaId = normalizeLegacyAppFormsTreeId(stripHiddenPrefix(rawAreaId));
  const matchedEntry = Object.entries(AREA_ID_PREFIX).find(([, prefix]) =>
    areaId.startsWith(prefix),
  );

  if (matchedEntry) {
    return matchedEntry[0];
  }

  if (appFormsTreeAreaNodeMapExtended[areaId]?.levelType === AutoLockFormsLevelType.FORM) {
    return AutoLockFormsLevelType.FORM;
  }

  return null;
};

export const getAreaNode = rawAreaId => {
  const areaId = normalizeLegacyAppFormsTreeId(stripHiddenPrefix(rawAreaId));

  return appFormsTreeAreaNodeMapExtended[areaId] || null;
};

export const getPageId = option =>
  `${option.section}-${option.subsection}-${option.tab}-${option.form}`;

export const getChipLabel = option => `${_.startCase(option?.form)} - ${option?.name}`;

const getPageIdToOptionsMap = createAppFormsTreeVersionedAccessor(() =>
  _.groupBy(appFormsTreeFieldOptionsExtended, getPageId),
);

export const detectVisibilityMode = (fieldIds, pageIds) => {
  const hasHiddenField = fieldIds?.some(checkIsHiddenField);
  const hasHiddenPage = pageIds?.some(checkIsHiddenField);

  return hasHiddenField || hasHiddenPage ? visibilityModeOptions[1] : visibilityModeOptions[0];
};

export const getAreaLabelPath = rawAreaId => {
  const node = getAreaNode(rawAreaId);

  if (!node) {
    return null;
  }

  const parts = [
    node.levelType !== AutoLockFormsLevelType.PAGE && node.pageLabel,
    node.levelType !== AutoLockFormsLevelType.PAGE &&
      node.levelType !== AutoLockFormsLevelType.SECTION &&
      node.sectionLabel,
    [AutoLockFormsLevelType.TAB, AutoLockFormsLevelType.FORM].includes(node.levelType) &&
      node.subsectionLabel,
    node.levelType === AutoLockFormsLevelType.FORM && node.tabLabel,
  ].filter(Boolean);

  // A single-child chain often repeats one label across its levels (Closing Information
  // is a subsection, a tab and a form), which would render as "A / A / A" on the chip.
  const pathSegments = [...parts, node.label].filter(
    (segment, index, segments) => segment !== segments[index - 1],
  );

  return {
    prefix: pathSegments.slice(0, -1).join(' / '),
    label: node.label,
    levelType: node.levelType,
  };
};

export const getInitialPageIds = ({ pageIds = [] }) => {
  const pageIdToOptionsMap = getPageIdToOptionsMap();

  // Ids saved before LEN2-499 may carry the nameless Lead/Activities/Tasks sections;
  // loading them under the restored names migrates the rule on its next save.
  return normalizeLegacyAppFormsTreeIds(pageIds.map(stripHiddenPrefix)).filter(
    areaId => !!appFormsTreeAreaNodeMapExtended[areaId] || !!pageIdToOptionsMap[areaId],
  );
};

export const getInitialFieldIds = ({ fieldIds = [] }) =>
  normalizeLegacyAppFormsTreeIds(fieldIds.map(stripHiddenPrefix))
    .map(fieldId => appFormsTreeFieldOptionsMapExtended[fieldId])
    .filter(Boolean);

export const getInitialUserFriendlyExpression = ({ rule, isNewExpressionsParsingLogicEnabled }) => {
  const userFriendlyBuiltExpression = UserFriendlyExpressionBuilder.build(
    rule.expression?.value,
    true,
    { isNewExpressionsParsingLogicEnabled },
  );

  return formatExpression(userFriendlyBuiltExpression);
};

export const collectAncestorSecurityGroupIds = (rulesMap, currentId) => {
  if (!currentId) {
    return [];
  }

  const currentRule = rulesMap[currentId];

  if (!currentRule) {
    return [];
  }

  const groupIds = currentRule.securityGroupIds || [];
  const rest = collectAncestorSecurityGroupIds(rulesMap, currentRule.parentId);

  return [...groupIds, ...rest];
};

export const formatInheritedSecurityGroups = (inheritedSecurityGroupIds, securityGroupsOptions) =>
  (inheritedSecurityGroupIds || []).reduce((acc, groupId) => {
    const group = securityGroupsOptions.find(({ id }) => id === groupId);

    if (group) {
      acc.push(group);
    }

    return acc;
  }, []);

const checkIsFieldVisible = (field, hiddenFieldsMap) => {
  if (field.type === FieldType.EMPTY) {
    return false;
  }

  if (field.type === FieldType.GROUP) {
    return !!field.fields?.some(nestedField => checkIsFieldVisible(nestedField, hiddenFieldsMap));
  }

  return !hiddenFieldsMap[field.fieldId];
};

export const checkHasVisibleFields = (fields, hiddenFieldsMap) =>
  fields.some(field => checkIsFieldVisible(field, hiddenFieldsMap));

export const checkIsTabHidden = (tabName, hiddenPageIds, hiddenFieldIds) => {
  const camelTabName = _.camelCase(tabName);
  const hiddenPageIdSet = new Set(hiddenPageIds.map(stripHiddenPrefix));

  const tabAreaNodes = appFormsTreeAreaNodesExtended.filter(
    node => node.levelType === AutoLockFormsLevelType.TAB && node.tab === camelTabName,
  );

  const hasAncestorOrSelfHiddenForTab = tabAreaNodes.some(
    tabNode =>
      hiddenPageIdSet.has(tabNode.id) || tabNode.ancestorIds.some(id => hiddenPageIdSet.has(id)),
  );

  if (hasAncestorOrSelfHiddenForTab) {
    return true;
  }

  const hasHiddenFormEntryForTab = hiddenPageIds.some(
    areaId => !getAreaLevelType(areaId) && areaId.split('-')[2] === camelTabName,
  );

  if (hasHiddenFormEntryForTab) {
    return true;
  }

  const tabFieldOptions = _.filter(
    appFormsTreeFieldOptionsMapExtended,
    option => option.tab === camelTabName,
  );

  if (_.isEmpty(tabFieldOptions)) {
    return false;
  }

  const hiddenSet = new Set(hiddenFieldIds);

  return tabFieldOptions.every(option => hiddenSet.has(option.value));
};

const getFormAreaNodesByName = createAppFormsTreeVersionedAccessor(() =>
  appFormsTreeAreaNodesExtended.reduce((acc, node) => {
    if (node.levelType !== AutoLockFormsLevelType.FORM) {
      return acc;
    }

    if (!acc[node.name]) {
      acc[node.name] = [];
    }

    acc[node.name].push(node);

    return acc;
  }, {}),
);

const getRelatedAreaIdsForForm = formName => {
  const formAreaNodesByName = getFormAreaNodesByName();
  const nodes = formAreaNodesByName[formName];

  if (!nodes) {
    return [];
  }

  return _.uniq(nodes.flatMap(node => [node.id, ...node.ancestorIds]));
};

export const checkIsFormPageLocked = ({ formName, pageIds }) => {
  if (!formName || !pageIds?.length) {
    return false;
  }

  const relatedAreaIds = new Set(getRelatedAreaIdsForForm(formName));

  return pageIds.some(pageId => {
    const areaId = stripHiddenPrefix(pageId);

    if (relatedAreaIds.has(areaId)) {
      return true;
    }

    return !getAreaLevelType(pageId) && pageId.includes(formName);
  });
};

export const checkIsAreaPageLocked = ({ areaName, levelType, pageIds, hiddenOnly = false }) => {
  if (!areaName || !levelType || !pageIds?.length) {
    return false;
  }

  const matchingNodes = appFormsTreeAreaNodesExtended.filter(
    node => node.levelType === levelType && node.name === areaName,
  );

  if (!matchingNodes.length) {
    return false;
  }

  const filteredPageIds = hiddenOnly ? pageIds.filter(checkIsHiddenField) : pageIds;
  const relevantIds = new Set(filteredPageIds.map(stripHiddenPrefix));

  return matchingNodes.some(
    node => relevantIds.has(node.id) || node.ancestorIds.some(id => relevantIds.has(id)),
  );
};
